import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
type PremiumSelectProps = {
  value: string;
  options: string[];
  onChange?: (value: string) => void;
  /** Optional field label rendered above the control. */
  label?: string;
  /** Compact toolbar styling (smaller padding) vs. full form-field styling. */
  compact?: boolean;
  /** Optional leading icon shown inside the trigger. */
  icon?: React.ElementType;
  /** Extra classes for the trigger button. */
  className?: string;
};
/**
 * Unified premium dropdown used across the app in place of native <select>.
 * Animated panel, frosted shadow, active-item check, click-outside + Escape close.
 */
export function PremiumSelect({
  value,
  options,
  onChange,
  label,
  compact = false,
  icon: Icon,
  className = ''
}: PremiumSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', handler);
      document.addEventListener('keydown', onKey);
    }
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const choose = (o: string) => {
    setOpen(false);
    onChange?.(o);
  };
  const triggerSize = compact ? 'px-3 py-2 text-sm' : 'px-4 py-3 text-sm';
  return <div className={label ? '' : 'relative'} data-id="element-2092">
      {label && <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-2093">
          {label}
        </label>}
      <div className="relative" ref={ref} data-id="element-2094">
        <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} className={`w-full flex items-center justify-between gap-2 bg-slate-50 border rounded-xl text-left font-medium transition-all ${triggerSize} ${open ? 'border-primary-indigo ring-2 ring-primary-indigo/20 bg-white text-slate-900' : 'border-slate-200 text-slate-600 hover:border-slate-300'} ${className}`} data-id="element-2095">
          <span className="flex items-center gap-2 min-w-0" data-id="element-2096">
            {Icon && <Icon className="w-4 h-4 text-slate-400 shrink-0" data-id="element-2097" />}
            <span className="truncate" data-id="element-2098">{value}</span>
          </span>
          <ChevronDownIcon className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180 text-primary-indigo' : ''}`} data-id="element-2099" />
        </button>
        <AnimatePresence data-id="element-2100">
          {open && <motion.ul role="listbox" initial={{
          opacity: 0,
          y: -6,
          scale: 0.98
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -6,
          scale: 0.98
        }} transition={{
          duration: 0.15
        }} className="absolute z-40 mt-2 min-w-full w-max max-w-[16rem] right-0 bg-white border border-slate-200 rounded-xl shadow-[0_12px_40px_rgb(0,0,0,0.12)] overflow-hidden py-1 max-h-72 overflow-y-auto" data-id="element-2101">
              {options.map(o => {
            const active = o === value;
            return <li key={o} role="option" aria-selected={active} data-id="element-2102">
                    <button type="button" onClick={() => choose(o)} className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-left transition-colors ${active ? 'bg-primary-indigo/5 text-primary-indigo font-semibold' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-2103">
                      <span className="truncate" data-id="element-2104">{o}</span>
                      {active && <CheckIcon className="w-4 h-4 text-primary-indigo shrink-0" data-id="element-2105" />}
                    </button>
                  </li>;
          })}
            </motion.ul>}
        </AnimatePresence>
      </div>
    </div>;
}